import React from "react";
import { FlatList, StyleSheet, View } from "react-native";
import { FormFieldProps } from "../FormFieldProps";
import { Button, IconButton, Text, useTheme } from "react-native-paper";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../../types/navigationParams";
import { useTranslation } from "react-i18next";
import { CollectionPickerOptions } from "../../screens/rentals/CollectionPickerScreen";
import { OutlinedInput } from "./OutlinedInput";
import { Where } from "../../services/firestoreServices";
import {Field} from "../../config/companies/config";

interface Item {
  id: number;
  [key: string]: any;
  relatedObjectField?: Item[];
}

type Props = Omit<FormFieldProps, "value"> & {
  value: Item[];
  field: Field & {
    relatedObject?: {
      collection: string;
      key: string;
      secondKey: string;
      multiselect?: boolean;
      countTotal?: boolean;
      formula?: string;
      where?: Where;
    };
  };
};

export function removeDuplicates(arr: Item[]): Item[] {
  if (!Array.isArray(arr)) {
    return [];
  }

  const uniqueIds = new Set();
  return arr.filter((item) => {
    if (uniqueIds.has(item.id)) {
      return false;
    }
    uniqueIds.add(item.id);
    return true;
  });
}

export const CalculateItemsField = ({
  field,
  value,
  onChange,
  formData,
}: Props) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { t } = useTranslation();
  const theme = useTheme();
  // Ensure value is always an array
  const normalizedValue = Array.isArray(value) ? value : [];
  const uniqueValues = removeDuplicates(normalizedValue);

  const openPicker = () => {
    navigation.navigate("CollectionPickerScreen", {
      onValueChange: (newItem: Item) =>
        onChange(field?.name, [...normalizedValue, newItem]),
      formData: formData,
      ...field,
    } as CollectionPickerOptions);
  };

  const openSecondPicker = (index: number) => {
    const changeObjValue = (selectedVal: Item[]) => {
      const currentItem = normalizedValue[index];
      const updatedItem: Item = {
        ...currentItem,
        relatedObjectField: selectedVal,
      };
      const updatedArray = normalizedValue.map((item, idx) =>
        idx === index ? updatedItem : item,
      );
      onChange(field.name, updatedArray);
    };

    navigation.navigate("CollectionPickerScreen", {
      onValueChange: changeObjValue,
      collection: field?.relatedObject?.collection || "",
      multiselect: field?.relatedObject?.multiselect,
      key: field.relatedObject?.key || "",
      secondKey: field.relatedObject?.secondKey,
      selectedOptions: normalizedValue[index].relatedObjectField || [],
      formData: normalizedValue[index],
      where: field.relatedObject?.where,
    });
  };

  const removeItem = (index: number) => {
    const newValue = normalizedValue.filter((_, idx) => index !== idx);
    onChange(field?.name, newValue);
  };

  const onIncrement = (item: Item) => {
    const newVal = [...normalizedValue, item];
    onChange(field.name, newVal);
  };

  const onDecrement = (item: Item) => {
    const newValIndex = normalizedValue.findIndex((obj) => obj.id === item.id);
    if (newValIndex !== -1) {
      const newValue = [...normalizedValue];
      newValue.splice(newValIndex, 1);
      onChange(field.name, newValue);
    }
  };

  const onChangeQuantity = (text: string, item: Item) => {
    const quantity = parseInt(text.replace(/[^0-9]/g, ""), 10) || 0;
    const newVal = Array(quantity).fill(item);
    const newValue = [...normalizedValue.filter((i) => i.id !== item.id), ...newVal];
    if (quantity > 0) {
      onChange(field.name, newValue);
    }
  };

  const renderRelatedObject = (item: Item[], itemIndex: number) => {
    const onPlus = (nestedItem: Item) => {
      const updatedItems = [...item, nestedItem];
      const currentItem = normalizedValue[itemIndex];
      const updatedItem: Item = {
        ...currentItem,
        relatedObjectField: updatedItems,
      };
      const newValue = normalizedValue.map((item, idx) =>
        idx === itemIndex ? updatedItem : item,
      );
      onChange(field.name, newValue);
    };

    const onMinus = (nestedItem: Item) => {
      const updatedItems = [...item];
      const index = updatedItems.findLastIndex((i) => i.id === nestedItem.id);
      if (index !== -1) {
        updatedItems.splice(index, 1);
        const currentItem = normalizedValue[itemIndex];
        const updatedItem: Item = {
          ...currentItem,
          relatedObjectField: updatedItems,
        };
        const newValue = normalizedValue.map((item, idx) =>
          idx === itemIndex ? updatedItem : item,
        );
        onChange(field.name, newValue);
      }
    };

    if (Array.isArray(item)) {
      const uniqueItems = removeDuplicates(item);

      return (
        <View style={styles.relatedObjectContainer}>
          {uniqueItems.map((obj, index) => {
            const currentItemCountInArray = item.filter(
              (i) => i.id === obj.id,
            ).length;
            return (
              <View key={index} style={styles.relatedObjectItem}>
                <View
                  style={[
                    styles.chip,
                    styles.relatedObjectChip,
                    { backgroundColor: theme.colors.secondaryContainer },
                  ]}
                >
                  <Text>{obj[field.relatedObject?.key || ""]}</Text>
                  <Text>{t(obj[field.relatedObject?.secondKey || ""])}</Text>
                </View>
                <View style={styles.counterContainer}>
                  <IconButton
                    size={20}
                    style={styles.counterButton}
                    icon="minus"
                    onPress={() => onMinus(obj)}
                    mode="contained-tonal"
                  />
                  <OutlinedInput
                    editable={false}
                    value={currentItemCountInArray.toString()}
                    outlineStyle={{ borderRadius: 8 }}
                    style={{ height: 40, width: 50 }}
                  />
                  <IconButton
                    size={20}
                    style={styles.counterButton}
                    icon="plus"
                    onPress={() => onPlus(obj)}
                    mode="contained-tonal"
                  />
                </View>
              </View>
            );
          })}
        </View>
      );
    }

    return (
      <View style={styles.relatedObjectChip}>
        <Text>{item[field.relatedObject?.key || ""]}</Text>
        <Text>{t(item[field.relatedObject?.secondKey || ""])}</Text>
      </View>
    );
  };

  return (
    <FlatList
      data={uniqueValues}
      keyExtractor={(item, index) => index.toString()}
      ListHeaderComponent={() => (
        <View style={styles.headerContainer}>
          <Text variant="titleMedium">{t(field?.label)}</Text>
          <Button onPress={openPicker} mode="text">
            {t("Add")}
          </Button>
        </View>
      )}
      renderItem={({ item, index }) => {
        const items = normalizedValue.filter((obj) => obj.id === item.id);

        return (
          <View style={styles.itemContainer}>
            <View style={styles.itemContent}>
              <View style={styles.itemHeader}>
                <View
                  style={[
                    styles.chip,
                    { backgroundColor: theme.colors.secondaryContainer },
                  ]}
                >
                  <Text>{item[field.key]}</Text>
                  <Text>{t(item[field.secondKey])}</Text>
                </View>
                <View style={styles.actionsContainer}>
                  {field.relatedObject ? (
                    <View style={styles.actionsContainer}>
                      <IconButton
                        style={styles.actionButton}
                        mode="contained-tonal"
                        size={20}
                        icon="plus"
                        onPress={() => openSecondPicker(index)}
                      />
                      <IconButton
                        style={styles.actionButton}
                        mode="contained-tonal"
                        size={20}
                        icon="close"
                        onPress={() => removeItem(index)}
                      />
                    </View>
                  ) : (
                    <View style={styles.actionsContainer}>
                      <IconButton
                        style={{ borderRadius: 8 }}
                        mode="contained-tonal"
                        size={20}
                        icon="minus"
                        onPress={() => onDecrement(item)}
                      />
                      <OutlinedInput
                        onChangeText={(text) => onChangeQuantity(text, item)}
                        value={items.length.toString()}
                        outlineStyle={{ borderRadius: 8 }}
                        style={{ height: 40, width: 50 }}
                      />
                      <IconButton
                        size={20}
                        style={styles.counterButton}
                        icon="plus"
                        onPress={() => onIncrement(item)}
                        mode="contained-tonal"
                      />
                    </View>
                  )}
                </View>
              </View>
              {field.relatedObject &&
                item.relatedObjectField &&
                renderRelatedObject(item.relatedObjectField, index)}
            </View>
          </View>
        );
      }}
    />
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  itemContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  itemContent: {
    flex: 1,
  },
  itemHeader: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  chip: {
    flex: 1,
    alignItems: "center",
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  actionsContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  actionButton: {
    borderRadius: 8,
    marginRight: 0,
  },
  counterContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  counterButton: {
    borderRadius: 8,
  },
  relatedObjectContainer: {
    gap: 8,
    marginTop: 8,
  },
  relatedObjectItem: {
    flexDirection: "row",
    height: 40,
    gap: 4,
    alignItems: "center",
    marginRight: -5,
  },
  relatedObjectChip: {
    marginLeft: 48,
  },
  singleRelatedObjectChip: {
    marginLeft: 48,
    marginTop: 8,
  },
});
